import { onScopeDispose, ref, watch } from 'vue'

import type { TooltipContentProps } from 'reka-ui'
import type { Directive } from 'vue'

// v-tooltip binds plain hover/focus listeners to the real DOM element after
// mount, so it works where the <Tooltip> wrapper cannot: on popover and menu
// triggers, where as-child prop forwarding drops a wrapper component's
// listeners. One TooltipHost instance (mounted in App.vue) renders the text
// anchored to whichever element is active - a falsy binding value shows
// nothing. Pass an object to place the tooltip on another side:
// v-tooltip="{ text: 'Jump', side: 'right' }".
//
// Vue drops directives inherited onto components whose render root is a
// fragment - every reka form control renders a VisuallyHiddenInput sibling, so
// v-tooltip on IconToggle, Checkbox and the like never reaches an element.
// Those wrappers expose a tooltip prop instead, wired to the same engine via
// useTooltip below.

// A structured payload for tooltips that need the roadmap hover's
// label/value/batch layout (e.g. the existing-fish icon, issue #2419) instead
// of a single line of text. TooltipHost and RoadmapTooltip both render it
// through TooltipRichContent.vue.
export type TooltipRichContent = {
  header?: string
  items: {
    label: string
    value: number | string
    unit?: string
    inputUnit?: string
    gapAbove?: boolean
    // Colors the value: positive (green) for income, negative (red) for cost
    tone?: 'positive' | 'negative'
  }[]
  batches?: { name: string; color: string | null }[]
}

export type TooltipBinding =
  | string
  | { text: string; side?: TooltipContentProps['side'] }
  | { rich: TooltipRichContent; side?: TooltipContentProps['side'] }
  | undefined

export const TOOLTIP_HOST_ID = 'app-tooltip-host'

const open = ref(false)
const text = ref('')
const rich = ref<TooltipRichContent>()
const side = ref<TooltipContentProps['side']>()
const reference = ref<HTMLElement | null>(null)

export const tooltipHostState = { open, text, rich, side, reference }

type TooltipEntry = { text?: string; rich?: TooltipRichContent; side?: TooltipContentProps['side'] }

const entries = new WeakMap<HTMLElement, TooltipEntry>()

// Every element carrying a tooltip right now, so the assistant can point at a control
// by its tooltip (tooltipControls below)
const attached = new Set<HTMLElement>()

const toEntry = (value: TooltipBinding): TooltipEntry => {
  if (typeof value === 'string') return { text: value }
  if (!value) return {}
  if ('rich' in value) return { rich: value.rich, side: value.side }
  return { text: value.text, side: value.side }
}

export type TooltipControl = { element: HTMLElement; label: string }

// The rendered elements with a tooltip and that tooltip as one line: the text, or the
// header of a rich tooltip. Elements inside a v-show'n or display:none subtree are left out.
export const tooltipControls = (): TooltipControl[] =>
  [...attached].flatMap((element) => {
    const entry = entries.get(element)
    const label = (entry?.text ?? entry?.rich?.header ?? '').replace(/\s+/g, ' ').trim()
    if (!label || !element.isConnected) return []
    if (!element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return []
    return [{ element, label }]
  })

const show = (el: HTMLElement) => {
  const entry = entries.get(el)
  if (!entry?.text && !entry?.rich) return
  reference.value = el
  text.value = entry.text ?? ''
  rich.value = entry.rich
  side.value = entry.side
  open.value = true
  el.setAttribute('aria-describedby', TOOLTIP_HOST_ID)
}

const hide = (el: HTMLElement) => {
  el.removeAttribute('aria-describedby')
  if (reference.value !== el) return
  open.value = false
}

const onPointerEnter = (event: PointerEvent) => {
  if (event.pointerType === 'touch') return
  show(event.currentTarget as HTMLElement)
}

const onPointerLeave = (event: PointerEvent) => {
  hide(event.currentTarget as HTMLElement)
}

const onFocusIn = (event: FocusEvent) => {
  const el = event.currentTarget as HTMLElement
  if (!el.matches(':focus-visible')) return
  show(el)
}

const onFocusOut = (event: FocusEvent) => {
  hide(event.currentTarget as HTMLElement)
}

// Activating the element (opening its popover, submitting) retires the
// tooltip, matching reka's own trigger behaviour.
const onDismissInteraction = (event: Event) => {
  hide(event.currentTarget as HTMLElement)
}

const attach = (el: HTMLElement) => {
  attached.add(el)
  el.addEventListener('pointerenter', onPointerEnter)
  el.addEventListener('pointerleave', onPointerLeave)
  el.addEventListener('focusin', onFocusIn)
  el.addEventListener('focusout', onFocusOut)
  el.addEventListener('pointerdown', onDismissInteraction)
  el.addEventListener('click', onDismissInteraction)
}

const detach = (el: HTMLElement) => {
  attached.delete(el)
  el.removeEventListener('pointerenter', onPointerEnter)
  el.removeEventListener('pointerleave', onPointerLeave)
  el.removeEventListener('focusin', onFocusIn)
  el.removeEventListener('focusout', onFocusOut)
  el.removeEventListener('pointerdown', onDismissInteraction)
  el.removeEventListener('click', onDismissInteraction)
  hide(el)
}

// Updates the stored entry and, when this element's tooltip is showing,
// re-labels it live - or hides it if the value went falsy.
const setEntry = (el: HTMLElement, value: TooltipBinding) => {
  const entry = toEntry(value)
  entries.set(el, entry)
  if (reference.value !== el || !open.value) return
  if (entry.text || entry.rich) {
    text.value = entry.text ?? ''
    rich.value = entry.rich
    side.value = entry.side
  } else {
    hide(el)
  }
}

export const vTooltip: Directive<HTMLElement, TooltipBinding> = {
  mounted(el, binding) {
    entries.set(el, toEntry(binding.value))
    attach(el)
  },
  updated(el, binding) {
    setEntry(el, binding.value)
  },
  unmounted(el) {
    detach(el)
  }
}

type TooltipTarget = Element | { $el: unknown } | null | undefined

// $el of a fragment-rooted component is its start anchor, not an element; the
// first element sibling is the component's real root (the same resolution reka
// uses in useForwardExpose).
const resolveTarget = (target: TooltipTarget): HTMLElement | null => {
  const node = target instanceof Element ? target : target?.$el
  if (node instanceof HTMLElement) return node
  if (node instanceof CharacterData) {
    return node.nextElementSibling instanceof HTMLElement ? node.nextElementSibling : null
  }
  return null
}

// Prop-based counterpart to v-tooltip for wrapper components whose internals
// are fragment-rooted. Pass getters for the target (template ref) and value.
export function useTooltip(target: () => TooltipTarget, value: () => TooltipBinding): void {
  let current: HTMLElement | null = null

  watch(
    () => resolveTarget(target()),
    (el) => {
      if (current) detach(current)
      current = el
      if (!el) return
      entries.set(el, toEntry(value()))
      attach(el)
    },
    { flush: 'post' }
  )

  watch(value, (binding) => {
    if (current) setEntry(current, binding)
  })

  onScopeDispose(() => {
    if (current) detach(current)
  })
}
